/*
 * Add-on bundle — dependent product toggle.
 *
 * (a) On product Add-to-Cart, when the toggle is ON and the submitted variant
 *     belongs to the main product, add the main product AND a linked add-on in
 *     one /cart/add.js call. The add-on line carries a private line-item
 *     property `_addon_for` = the main product's ID.
 * (b) Whenever the cart changes, keep each add-on line's quantity equal to its
 *     linked main product's quantity (removing the add-on when the main is gone),
 *     and remove any configured add-on product (window.AddonBundle.addonProductIds)
 *     that was added WITHOUT the toggle (i.e. has no _addon_for link).
 */
(() => {
  const ADDON_PROPERTY = '_addon_for'

  /* ---- (a) Capture-phase ATC interceptor -------------------------------- */
  // Capture phase: this runs before the form's inline `onsubmit="onSubmitAtcForm(...)"`.
  document.addEventListener('submit', (event) => {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return

    const idField = form.querySelector('[name="id"]')
    if (!idField) return // not a product add-to-cart form

    const toggle = document.querySelector('addon-bundle-toggle')
    if (!toggle) return // not the add-on template

    const input = toggle.querySelector('.addon-bundle__input')
    if (!input || !input.checked) return // toggle off -> let the native flow add only the main product

    let mainVariantIds = []
    try {
      mainVariantIds = JSON.parse(toggle.dataset.mainVariantIds || '[]')
    } catch (e) {
      return
    }
    const submittedVariantId = parseInt(idField.value, 10)
    if (!mainVariantIds.includes(submittedVariantId)) return // a different product's form -> ignore

    // Take over: stop the inline native handler and the browser submit.
    event.preventDefault()
    event.stopImmediatePropagation()
    bundleAddToCart(form, toggle, submittedVariantId)
  }, true)

  async function bundleAddToCart(form, toggle, mainVariantId) {
    const addonVariantId = parseInt(toggle.dataset.addonVariantId, 10)
    const mainProductId = toggle.dataset.mainProductId
    if (!mainVariantId || !addonVariantId) return

    const qtyField = form.querySelector('[name="quantity"]')
    let mainQty = parseInt(qtyField ? qtyField.value : '1', 10)
    if (!mainQty || mainQty < 1) mainQty = 1

    const payload = {
      items: [
        { id: mainVariantId, quantity: mainQty },
        { id: addonVariantId, quantity: mainQty, properties: { [ADDON_PROPERTY]: mainProductId } }
      ]
    }

    const button = form.querySelector('.btn-atc')
    const originalHtml = button ? button.innerHTML : null
    if (button) {
      button.setAttribute('disabled', 'disabled')
      button.innerHTML = '<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">Loading...</span></div>'
    }

    let response
    try {
      response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
    } catch (e) {
      if (button) { button.removeAttribute('disabled'); if (originalHtml !== null) button.innerHTML = originalHtml }
      return
    }

    if (typeof window.refreshCartContents === 'function') {
      window.refreshCartContents(response)
    }
    if (button) { button.removeAttribute('disabled'); if (originalHtml !== null) button.innerHTML = originalHtml }

    if (response.ok && typeof bootstrap !== 'undefined' && bootstrap.Offcanvas) {
      const drawer = document.querySelector('#offcanvas-cart')
      if (drawer) bootstrap.Offcanvas.getOrCreateInstance(drawer).show()
    }
  }

  /* ---- (b) Dependency sync --------------------------------------------- */
  let isSyncing = false

  async function syncAddonDependencies() {
    if (isSyncing) return
    isSyncing = true
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
      const cart = await res.json()
      const items = cart.items || []

      // Total quantity of each product currently in the cart, keyed by product_id.
      const qtyByProduct = {}
      for (const li of items) {
        qtyByProduct[li.product_id] = (qtyByProduct[li.product_id] || 0) + li.quantity
      }

      // Product IDs configured as dependent add-ons (can't be bought on their own).
      const addonProductIds = new Set(
        window.AddonBundle && Array.isArray(window.AddonBundle.addonProductIds)
          ? window.AddonBundle.addonProductIds
          : []
      )

      // Decide each add-on line's target quantity:
      //  - linked add-on (has _addon_for): match its main product's total qty (0 => remove)
      //  - configured add-on with NO link (added on its own): remove (0)
      const changes = []
      for (const li of items) {
        const parent = li.properties && li.properties[ADDON_PROPERTY]
        let targetQty = null
        if (parent) {
          targetQty = qtyByProduct[Number(parent)] || 0
        } else if (addonProductIds.has(li.product_id)) {
          targetQty = 0
        }
        if (targetQty !== null && li.quantity !== targetQty) {
          changes.push({ id: li.key, quantity: targetQty })
        }
      }
      if (changes.length === 0) return

      let lastResponse
      for (const change of changes) {
        lastResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(change)
        })
      }
      if (lastResponse && typeof window.refreshCartContents === 'function') {
        window.refreshCartContents(lastResponse)
      }
    } catch (e) {
      /* ignore */
    } finally {
      isSyncing = false
    }
  }

  // After any cart change (the theme dispatches this once refreshCartContents finishes).
  window.addEventListener('updated.dev-dnk.cart', syncAddonDependencies)
  // Defensive catch-up once on load (handles a main removed where the sync didn't run).
  document.addEventListener('DOMContentLoaded', syncAddonDependencies)
})()
